import { useMemo, useState } from 'react';
import { computeAmount, type TimeEntry } from '../../services/timeTracking/timeTrackingService';

import { formatAppDate } from '../../lib/dateFormat';
import { ResizableColumns } from '../ui/ResizableColumns';
import { Avatar } from '../ui/Avatar';
import {
  ListEmpty,
  ListFooter,
  ListPagination,
  ListToolbar,
  ToolbarGrow,
  ToolbarSearch,
} from '../ui/ListToolbar';
interface Props {
  entries: TimeEntry[];
  dossierLabels: Record<string, string>;
  /** Référence (numéro, RG) et titre du dossier, sur deux lignes. */
  dossierRefs?: Record<string, { ref: string | null; title: string }>;
  collaboratorNames: Record<string, string>;
  onOpen: (entry: TimeEntry) => void;
  /** Filtres de la page, placés après ceux du tableau. */
  leading?: React.ReactNode;
  /** Actions de la page, à droite de la barre d'outils. */
  trailing?: React.ReactNode;
}

const PAGE = 25;

function clock(minutes: number): string {
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return `${h}:${String(m).padStart(2, '0')} h`;
}

function euros(value: number): string {
  return value.toLocaleString('fr-FR', { style: 'currency', currency: 'EUR' });
}

export function TimesheetLines({ entries, dossierLabels, dossierRefs, collaboratorNames, onOpen, leading, trailing }: Props) {
  const [page, setPage] = useState(0);
  const [query, setQuery] = useState('');
  const [status, setStatus] = useState<'all' | 'open' | 'invoiced'>('all');

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return entries.filter((e) => {
      if (status === 'open' && e.invoiced_in_id) return false;
      if (status === 'invoiced' && !e.invoiced_in_id) return false;
      if (!q) return true;
      const blob = `${e.description ?? ''} ${e.billing_code ?? ''} ${dossierLabels[e.dossier_id] ?? ''}`.toLowerCase();
      return blob.includes(q);
    });
  }, [entries, query, status, dossierLabels]);

  const pages = Math.max(1, Math.ceil(filtered.length / PAGE));
  const safePage = Math.min(page, pages - 1);
  const slice = filtered.slice(safePage * PAGE, safePage * PAGE + PAGE);

  return (
    <div className="space-y-3">
      <ListToolbar>
        <ToolbarSearch
          value={query}
          onChange={(value) => { setQuery(value); setPage(0); }}
          placeholder="Rechercher par type ou description"
          ariaLabel="Rechercher une prestation"
          className="w-[250px]"
        />
        <select
          aria-label="Statut de facturation"
          value={status}
          onChange={(e) => { setStatus(e.target.value as typeof status); setPage(0); }}
          className="h-9 w-[120px] rounded-full border border-neutral-200 bg-white px-3 text-[13px] text-cime-text-primary hover:border-neutral-300 focus:border-cime-primary focus:outline-none"
        >
          <option value="all">Tous</option>
          <option value="open">Non facturé</option>
          <option value="invoiced">Facturé</option>
        </select>
        {leading}
        <ToolbarGrow />
        {trailing}
      </ListToolbar>
      <div className="overflow-hidden rounded-xl border border-neutral-200 bg-white">
      <div className="overflow-x-auto">
        <table className="cime-table text-[13px]">
          <ResizableColumns id="timesheet-lines" />
          <thead>
            <tr className="text-left">
              <th className="px-2.5 py-2 first:pl-[18px] last:pr-[18px]">Prestation</th>
              <th className="px-2.5 py-2 first:pl-[18px] last:pr-[18px]">Dossier</th>
              <th className="px-2.5 py-2 first:pl-[18px] last:pr-[18px]">Producteur</th>
              <th className="px-2.5 py-2 first:pl-[18px] last:pr-[18px] text-right">Durée</th>
              <th className="px-2.5 py-2 first:pl-[18px] last:pr-[18px] text-right">Taux</th>
              <th className="px-2.5 py-2 first:pl-[18px] last:pr-[18px] text-right">Montant</th>
              <th className="px-2.5 py-2 first:pl-[18px] last:pr-[18px]">Statut</th>
              <th className="px-2.5 py-2 first:pl-[18px] last:pr-[18px]">Date</th>
            </tr>
          </thead>
          <tbody>
            {slice.map((e) => {
              const amount = computeAmount(e);
              const producer = collaboratorNames[e.collaborator_id];
              const dossierRef = dossierRefs?.[e.dossier_id];
              return (
                <tr key={e.id} className="cursor-pointer" onClick={() => onOpen(e)}>
                  <td className="max-w-[360px] px-2.5 py-[9px] first:pl-[18px] last:pr-[18px] leading-tight">
                    <span className="block text-[10px] font-semibold uppercase tracking-[0.06em] text-neutral-400">
                      {e.billing_code || 'Temps passé'}
                    </span>
                    <span className="block truncate text-[13px] text-cime-text-primary">{e.description || '—'}</span>
                  </td>
                  <td className="max-w-[260px] px-2.5 py-[9px] leading-tight">
                    {dossierRef ? (
                      <>
                        {dossierRef.ref ? (
                          <span className="block truncate text-[11px] tracking-[0.04em] text-neutral-400">
                            {dossierRef.ref}
                          </span>
                        ) : null}
                        <span className="block truncate text-[13px] text-cime-text-primary">{dossierRef.title}</span>
                      </>
                    ) : (
                      <span className="block truncate text-[13px] text-cime-text-primary">{dossierLabels[e.dossier_id] ?? '—'}</span>
                    )}
                  </td>
                  <td className="whitespace-nowrap px-2.5 py-[9px] first:pl-[18px] last:pr-[18px]">
                    {producer ? (
                      <span className="flex items-center gap-2 text-[13px] text-cime-text-primary">
                        <Avatar name={producer} size="xs" className="h-[22px] w-[22px] text-[10px]" />
                        {producer}
                      </span>
                    ) : (
                      '—'
                    )}
                  </td>
                  <td className="whitespace-nowrap px-2.5 py-[9px] first:pl-[18px] last:pr-[18px] text-right font-semibold tabular-nums">{clock(e.duration_minutes)}</td>
                  <td className="whitespace-nowrap px-2.5 py-[9px] first:pl-[18px] last:pr-[18px] text-right tabular-nums text-cime-text-muted">
                    {e.hourly_rate_snapshot != null ? euros(Number(e.hourly_rate_snapshot)) : '—'}
                  </td>
                  <td className="whitespace-nowrap px-2.5 py-[9px] first:pl-[18px] last:pr-[18px] text-right tabular-nums">{amount != null ? euros(amount) : '—'}</td>
                  <td className="whitespace-nowrap px-2.5 py-[9px] first:pl-[18px] last:pr-[18px]">
                    <span className={`inline-flex items-center gap-1.5 text-[13px] font-medium ${e.invoiced_in_id ? 'text-emerald-800' : 'text-[#9a3d22]'}`}>
                      <i aria-hidden className={`h-1.5 w-1.5 rounded-full ${e.invoiced_in_id ? 'bg-emerald-700' : 'bg-[#c8431f]'}`} />
                      {e.invoiced_in_id ? 'Facturé' : 'Non facturé'}
                    </span>
                  </td>
                  <td className="whitespace-nowrap px-2.5 py-[9px] first:pl-[18px] last:pr-[18px] text-[13px] text-cime-text-primary">{formatAppDate(e.started_at)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {slice.length === 0 ? <ListEmpty>Aucune prestation.</ListEmpty> : null}
      <ListFooter pagination={<ListPagination page={safePage} pageCount={pages} onChange={setPage} />}>
        {filtered.length === 0
          ? 'Aucun résultat'
          : `${safePage * PAGE + 1}–${safePage * PAGE + slice.length} sur ${filtered.length} · tri : date ↓`}
      </ListFooter>
      </div>
    </div>
  );
}
